import Vue from 'vue';
import {VueModalComponent} from '../ModalService/VueModalComponent';
import {I18NGetter} from '@/services/enumTranslator/I18NGetter';
import {AutomationTestsIds} from '@/env/AutomationTests/ids/AutomationTestsIds';
import {HardConfirmModalData} from '@/components/fpComponents/services/ModalService/hardConfirmModal';
import HardConfirmModal from '@/components/fpComponents/services/ModalService/hardConfirmModal/HardConfirmModal.vue';
import {useModalStore} from '@/components/L/Modal/useModalStore';
import {ModalInstanceOld} from '@/components/L/Modal/ModalTypes';
import IframeModal from '@/components/L/Modal/iframeModal.vue';
interface ModalOptions {
  maxWidth: number,
  persistent: boolean,
  fullscreen?: boolean,
  contentClass?: string,
  style?: string,
  transition?: string | false,
}

export type IframeModalName =
  'NEW_DEAL_MODAL' |
  'NEW_CLIENT_MODAL' |
  'DELEGATE_DEAL_MODAL' |
  'NEW_DEAL_WITH_CLIENT_CREATION_MODAL' |
  'CLIENT_AGREEMENTS_MODAL' |
  'CART_MODAL';

const createModalTemplate = () => {
  return '' +
    '<v-dialog ref="dialog" @keydown.esc="closeModal()" :persistent="true" scrollable v-model="showDialog" ' +
    ' v-if="showDialog" :max-width="typeof modalOptions.maxWidth == \'number\' ? (modalOptions.maxWidth + \'px\') : modalOptions.maxWidth"' +
    ' :fullscreen="modalOptions.fullscreen" :content-class="modalOptions.fullscreen ? \'rounded-0! \' + (modalOptions.contentClass || \'\') : modalOptions.contentClass || \'\'"' +
    ' :transition="modalOptions.transition === undefined ? \'dialog-transition\' : modalOptions.transition"' +
    ' :style="modalOptions.style || \'\'">' +
    '<v-btn icon v-if="!modalOptions.persistent" @click="closeModal()" style="position: absolute; top: 4px; right: 5px; z-index: 1000;">' +
    '<v-icon>close</v-icon>' +
    '</v-btn>' +
    '<ModalComponent ref="modal" :modal-data="modalData" :show-dialog.sync="showDialog"/>' +
    '</v-dialog>';
};

type CloseModalActionData = {
  ctx: any;
  data: any;
  resolve: (data?: any) => void;
  modalInstance: ModalInstanceOld;
}

export const closeModalAction = (closeModalData: CloseModalActionData) => {
  const {ctx, data, resolve, modalInstance} = closeModalData;
  const modalStore = useModalStore()
  ctx.$emit('update:showDialog', false);
  const activeDialogs = document.querySelectorAll('.v-dialog__content--active');
  const revealedDialog = activeDialogs[activeDialogs.length - 2] as HTMLElement | undefined;
  // Reveal only via the display property. Rewriting the whole cssText
  // (the old substring(0, 13) hack) corrupted the z-index/transition state of
  // dialogs still animating when closes overlap (QA white-screen bug).
  revealedDialog?.style.removeProperty('display');
  // Remove ONLY this dialog's own scrim (Vuetify's overlayable keeps it as
  // `overlay` on the VDialog instance). A global '.v-overlay--active' lookup
  // grabs the NEWEST active overlay (Vuetify prepends scrims), which strips
  // the scrim from a dialog that is still opening when closes race an open.
  const vDialog = ctx.$refs?.dialog ?? ctx.$parent?.$refs?.dialog;
  vDialog?.overlay?.$el?.remove();
  resolve(data);
  modalStore.closeModal(modalInstance);
}

// @deprecated please use useModal
export class ModalService {
  // eslint-disable-next-line vue/max-len
  public async openHardConfirm(title: string, falseText: string = I18NGetter().useButtonCommons.CANCEL, trueText: string = I18NGetter().useButtonCommons.YES_I_CONFIRM, maxWidth: number = 450): Promise<boolean | undefined> {
    return this.open<boolean, HardConfirmModalData>(HardConfirmModal, {
      title, trueText, falseText,
    }, {
      maxWidth: 500,
      persistent: true,
    });
  }

  // LEAD-PASS_LEAD_INFO_1 -> klasa generowana przez Lende potrzebna do złapania elementu.
  // eslint-disable-next-line vue/max-len
  public openConfirmModal(msg: string | {title?: string, description?: string}, falseText: string = I18NGetter().useButtonCommons.NO, trueText: string = I18NGetter().useButtonCommons.YES, maxWidth: number = 450, trueColor: string = 'primary'): Promise<boolean | undefined> {
    return this.open<boolean>({
      // `ph-no-capture` on title + description: confirm modals are reused
      // app-wide and callers occasionally interpolate dynamic data (e.g.
      // `dealName`) which would otherwise reach PostHog autocapture as
      // visible text. Yes/No button clicks stay capturable.
      template:
        '<v-card class="pa-1 confirm-modal-card">' +
        '<v-card-title style="word-break: break-word" class="headline ph-no-capture" v-html="msg.title || msg"></v-card-title>' +
        '<v-card-text v-if="msg.description" v-html="msg.description" class="ph-no-capture" style="word-break: break-word"></v-card-text>' +
        '<v-card-actions style="width: 100%" class="pt-0">' +
        '<v-spacer></v-spacer>' +
        '<v-btn outlined class="text-none my-2" data-test="' + AutomationTestsIds.confirmModal.falseButton + '" @click="closeModal(false)">{{falseText}}</v-btn>' +
        '<v-btn depressed :color="trueColor" class="text-none my-2 LEAD-PASS_LEAD_INFO_1" data-test="' + AutomationTestsIds.confirmModal.trueButton + '" @click="closeModal(true)">{{trueText}}</v-btn>' +
        '</v-card-actions>' +
        '</v-card>',
      data() {
        return {
          msg,
          falseText,
          trueText,
          trueColor,
        };
      },
    }, undefined, {
      persistent: true,
      maxWidth: maxWidth,
      fullscreen: false,
    })!;
  }

  // eslint-disable-next-line vue/max-len
  public open<T, TData extends any = any>(modalComponent: any, modalData: Partial<TData> = {}, modalOptions: Partial<ModalOptions> = {}): Promise<T> | undefined {
    const modalStore = useModalStore()
    const defaultModalOptions: ModalOptions = {
      maxWidth: 650,
      persistent: false,
      fullscreen: undefined,
    };
    modalOptions = Object.assign(defaultModalOptions, modalOptions);
    return new Promise<T>((resolve) => {
      const extendedModalComponent = Vue.extend(VueModalComponent).extend(modalComponent).extend({
        methods: {
          closeModal(data?: any) {
            closeModalAction({ctx: this, data, resolve, modalInstance});
          },
        },
      });
      const TempComponent = Vue.extend({
        data() {
          return {
            showDialog: true,
            modalData,
            modalOptions,
          };
        },
        components: {
          ModalComponent: extendedModalComponent,
        },
        methods: {
          closeModal(data?: any) {
            closeModalAction({ctx: this, data, resolve, modalInstance});
          },
        },
        template: createModalTemplate(),
      });
      const modalInstance: ModalInstanceOld = {
        formComponent: TempComponent,
        uuid: crypto.randomUUID(),
        modalData: modalData,
      }
      modalStore.addModal(modalInstance)
    });
  }

  public openIframeModal<T, TData extends { name: IframeModalName, url?: string } = any>(modalData: Partial<TData>, modalOptions: Partial<ModalOptions> = {}): Promise<T> | undefined {
    const modalStore = useModalStore();

    const sameModal = modalStore.modals.find(modal => {
      const oldModal = modal as ModalInstanceOld;
      const oldModalData = oldModal?.modalData as { name: IframeModalName };
      return oldModalData?.name === modalData?.name;
    });

    if (sameModal) {
      modalStore.closeModal(sameModal as ModalInstanceOld, null);
    }

    // No enter transition for iframe modals: the V3 iframe boot starves the
    // main thread (same-origin on QA/prod), freezing the dialog at the
    // transition's first frame (opacity 0) - the user sees a white screen
    // until the boot ends. Without the transition the dialog paints its
    // surface and loader instantly, and the boot happens behind them.
    return this.open<T>(IframeModal, modalData, {transition: false, ...modalOptions});
  }
}

export default new ModalService();
